import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { billingApi } from '../../../services/billingApi';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import { Alert, Button, Modal, Select, Textarea, TextInput } from '../../../components/common';
import { SectionCard, EmptyState } from './components';
import { formatCurrency, formatDateOnly, kindLabel } from './format';
import './BillingAdmin.css';

const KIND_OPTIONS = [
  { value: 'per_query', label: 'Per query' },
  { value: 'per_mb_payload', label: 'Per MB payload' },
  { value: 'per_1k_tokens', label: 'Per 1K tokens' },
  { value: 'flat_monthly', label: 'Flat monthly' },
  { value: 'flat_yearly', label: 'Flat yearly' },
];

const EMPTY_PLAN = {
  name: '',
  currency: 'USD',
  notes: '',
  lineItems: [
    { kind: 'per_query', unitPriceCents: 50, includedQuantity: 0, label: 'Per query', notes: '' },
  ],
};

export default function PricingPlansPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', planId? }
  const [form, setForm] = useState(EMPTY_PLAN);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (!currentUserHasCerebralAdminRole()) navigate('/user-home');
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.listPricingPlans();
      setPlans(data?.plans || data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_PLAN);
    setFormError(null);
    setModal({ mode: 'create' });
  };

  const openEdit = async (planId) => {
    setBusy(true);
    setFormError(null);
    try {
      const data = await billingApi.getPricingPlan(planId);
      const plan = data?.plan || data;
      setForm({
        name: plan?.Name || plan?.name || '',
        currency: plan?.Currency || plan?.currency || 'USD',
        notes: plan?.Notes || plan?.notes || '',
        lineItems: (plan?.LineItems || plan?.line_items || []).map((li) => ({
          kind: li.Kind || li.kind,
          unitPriceCents: li.UnitPriceCents ?? li.unit_price_cents ?? 0,
          includedQuantity: li.IncludedQuantity ?? li.included_quantity ?? 0,
          label: li.Label || li.label || '',
          notes: li.Notes || li.notes || '',
        })),
      });
      setModal({ mode: 'edit', planId });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load plan');
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setFormError(null);
  };

  const updateLine = (idx, patch) => {
    setForm({ ...form, lineItems: form.lineItems.map((li, i) => (i === idx ? { ...li, ...patch } : li)) });
  };
  const addLine = () => setForm({
    ...form,
    lineItems: [
      ...form.lineItems,
      { kind: 'flat_monthly', unitPriceCents: 0, includedQuantity: 0, label: '', notes: '' },
    ],
  });
  const removeLine = (idx) => setForm({ ...form, lineItems: form.lineItems.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      if (!form.name.trim()) throw new Error('Plan name is required.');
      if (!form.lineItems.length) throw new Error('At least one line item is required.');
      const payload = {
        name: form.name,
        currency: (form.currency || 'USD').toUpperCase(),
        notes: form.notes || null,
        lineItems: form.lineItems.map((li) => ({
          kind: li.kind,
          unitPriceCents: Number(li.unitPriceCents) || 0,
          includedQuantity: Number(li.includedQuantity) || 0,
          label: li.label || null,
          notes: li.notes || null,
        })),
      };
      if (modal?.mode === 'edit' && modal.planId) {
        await billingApi.updatePricingPlan(modal.planId, payload);
      } else {
        await billingApi.createPricingPlan(payload);
      }
      setModal(null);
      setForm(EMPTY_PLAN);
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const modalTitle = modal?.mode === 'edit' ? 'Edit pricing plan' : 'Create pricing plan';

  return (
    <div className="user-home-layout__page">
      <Link to="/user-home/admin/billing" className="billing-admin__breadcrumb">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        Back to dashboard
      </Link>

      <div className="billing-admin__page-header">
        <div className="billing-admin__header-text">
          <h1 className="user-home-layout__page-title">Pricing plans</h1>
          <p className="user-home-layout__page-subtitle">
            Reusable pricing templates. Each plan is a bag of line items (per query, per MB, flat
            fee) and is bound to a CompanyAccount via a subscription.
          </p>
        </div>
        <div className="billing-admin__header-actions">
          <Button variant="primary" onClick={openCreate}>+ New pricing plan</Button>
        </div>
      </div>

      {error && (
        <Alert variant="error" title="Could not load pricing plans" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <div className="billing-admin__plan-grid">
          {Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="billing-admin__plan-card">
              <span className="billing-admin__skeleton billing-admin__skeleton--lg" style={{ width: '60%' }} />
              <span className="billing-admin__skeleton" />
              <span className="billing-admin__skeleton" />
              <span className="billing-admin__skeleton" style={{ width: '40%' }} />
            </div>
          ))}
        </div>
      )}

      {!loading && plans.length === 0 && (
        <SectionCard>
          <EmptyState
            icon={
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" />
                <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
              </svg>
            }
            title="No pricing plans yet"
            message="Create a pricing plan to bind a customer account to a per-query, per-payload, or flat fee model."
            action={<Button variant="primary" onClick={openCreate}>+ Create pricing plan</Button>}
          />
        </SectionCard>
      )}

      {!loading && plans.length > 0 && (
        <div className="billing-admin__plan-grid">
          {plans.map((p) => {
            const planId = p.PricingPlanId || p.pricing_plan_id;
            const lineItems = p.LineItems || p.line_items || [];
            return (
              <article key={planId} className="billing-admin__plan-card">
                <header className="billing-admin__plan-card-header">
                  <h3 className="billing-admin__plan-name">{p.Name || p.name}</h3>
                  <span className="billing-admin__plan-currency">{p.Currency || p.currency || 'USD'}</span>
                </header>
                {p.Notes || p.notes ? (
                  <p className="billing-admin__muted" style={{ margin: 0 }}>{p.Notes || p.notes}</p>
                ) : null}
                {lineItems.length === 0 ? (
                  <p className="billing-admin__muted" style={{ margin: 0 }}>No line items configured.</p>
                ) : (
                  <ul className="billing-admin__plan-lineitems">
                    {lineItems.map((li, idx) => {
                      const kind = li.Kind || li.kind;
                      const cents = li.UnitPriceCents ?? li.unit_price_cents ?? 0;
                      const label = li.Label || li.label || kindLabel(kind);
                      return (
                        <li key={idx} className="billing-admin__plan-lineitem">
                          <span className={`billing-admin__lineitem-rail billing-admin__lineitem-rail--${kind}`} />
                          <span className={`billing-admin__kind-chip billing-admin__kind-chip--${kind}`}>{kindLabel(kind)}</span>
                          <span style={{ flex: 1 }}>{label}</span>
                          <strong>{formatCurrency(cents, p.Currency || p.currency || 'USD')}</strong>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <footer className="billing-admin__plan-card-footer">
                  <span>Created {formatDateOnly(p.CreatedAt || p.created_at)}</span>
                  <Button variant="ghost" onClick={() => openEdit(planId)}>Edit</Button>
                </footer>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={!!modal}
        onClose={closeModal}
        title={modalTitle}
        size="large"
        footer={
          <>
            <Button variant="ghost" onClick={closeModal} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={busy}>
              {busy ? 'Saving…' : modal?.mode === 'edit' ? 'Save changes' : 'Create plan'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit}>
          {formError && <Alert variant="error">{formError}</Alert>}

          <div className="billing-admin__form-grid">
            <TextInput
              variant="modal"
              label="Plan name *"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Acme Tier 1"
            />
            <TextInput
              variant="modal"
              label="Currency *"
              hint="3-letter ISO 4217 code, e.g. USD."
              maxLength={3}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <Textarea
              variant="modal"
              label="Internal notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <h4 className="billing-admin__section-title" style={{ marginTop: 24, marginBottom: 12 }}>Line items</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {form.lineItems.map((li, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
                <span className={`billing-admin__lineitem-rail billing-admin__lineitem-rail--${li.kind}`} />
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Select
                    variant="modal"
                    label="Kind"
                    value={li.kind}
                    onChange={(e) => updateLine(idx, { kind: e.target.value })}
                    options={KIND_OPTIONS}
                    placeholder=""
                  />
                  <TextInput
                    variant="modal"
                    label="Display label"
                    value={li.label}
                    onChange={(e) => updateLine(idx, { label: e.target.value })}
                    placeholder={kindLabel(li.kind)}
                  />
                  <TextInput
                    variant="modal"
                    label="Unit price (¢)"
                    hint={`Equivalent to ${formatCurrency(li.unitPriceCents || 0, form.currency)} per unit.`}
                    type="number"
                    value={li.unitPriceCents}
                    onChange={(e) => updateLine(idx, { unitPriceCents: e.target.value })}
                  />
                  <TextInput
                    variant="modal"
                    label="Included quantity"
                    hint="Free-tier units before billing kicks in."
                    type="number"
                    value={li.includedQuantity}
                    onChange={(e) => updateLine(idx, { includedQuantity: e.target.value })}
                  />
                </div>
                <Button
                  variant="ghost"
                  onClick={() => removeLine(idx)}
                  disabled={form.lineItems.length === 1}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Button variant="secondary" onClick={addLine}>+ Add line item</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
